import React, { useEffect, Fragment } from "react";
import { push } from "connected-react-router";
import { connect, useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";
import { arrayOf, bool, func, number, shape, string } from "prop-types";

import { selectors as sessionSelectors } from "reducers/session";
import { actions as depositsActions, selectors as depositsSelectors } from "reducers/deposits";
import { actions as menuActions } from "reducers/drawer";
import { getLimitDate } from "util/date";

import Notification from "pages/_components/Notification";
import MainContainer from "pages/_components/MainContainer";
import DepositList from "pages/deposits/_components/DepositList";
import HistoricalList from "pages/deposits/_components/HistoricalList";
import I18n from "pages/_components/I18n";
import CustomTabs from "pages/_components/CustomTabs/CustomTabs";
import OptionsHeader from "pages/_components/Options";
import DepositOptions from "pages/deposits/_components/Options";
import DepositAction from "pages/deposits/_components/DepositAction";

import CustomProductHead from "pages/_components/CustomProductHead";
import ViewTitle from "pages/_components/header/components/ViewTitle";
import Button from "pages/_components/Button";
import Dropdown from "pages/_components/Dropdown";
import gaEventTracker, { events } from "util/gaEventTracker";
import CustomButton from "pages/_components/CustomButton/CustomButton";
import { isIOS } from "react-device-detect";

const Deposits = ({
    dispatch,
    deposits,
    noDepositFound,
    totalInvestedCapital,
    fetching,
    isRequestAvailable,
    toggleOptions,
    depositsScreenToHistorical,
    historicalDeposits,
    filters,
    fetchingDownload,
    history,
    ...props
}) => {
    const isLoading = fetching && !deposits.length;
    const { depositSelectedTab, historicalDepositsFetching } = useSelector((state) => state.deposits);
    const { isDesktop } = props;

    const tabsData = [
        {
            text: "deposits.currentTermDeposits",
            textType: "I18N",
            downloadData: {
                active: true,
                filters: {},
                deposits,
            },
        },
        {
            text: "deposits.historyQuery",
            textType: "I18N",
            downloadData: {
                active: false,
                filters,
                deposits: historicalDeposits,
            },
        },
    ];

    useEffect(() => {
        dispatch(depositsActions.listDeposits());
        dispatch(depositsActions.cleanHistoricalDeposits());
        dispatch(depositsActions.setDepositData({}));
        dispatch(
            depositsActions.listHistoricalDeposits({
                ...filters,
                dueDateFrom: getLimitDate(-3652),
                dueDateTo: getLimitDate(0),
            }),
        );
    }, []);

    const handleCloseOptions = () => {
        if (toggleOptions.length) {
            dispatch(depositsActions.toggleOptions([]));
        }
    };

    const handleDepositListDownload = (active, filter, format) => {
        gaEventTracker.triggerEvent(events.pf_listado_descargar());
        dispatch(depositsActions.downloadDepositList(active, filter, format));
    };

    const renderDownloadDropdown = () => {
        if (!isEmpty(deposits)) {
            return isDesktop ? (
                <div className="toolbar-item download" key="exportList">
                    <Dropdown
                        image="images/figma/icon/download.svg"
                        label="global.download"
                        buttonClass="btn btn-outline"
                        pullRight
                        fetching={fetchingDownload}>
                        <Button
                            onClick={() => handleDepositListDownload(depositSelectedTab, filters, "PDF")}
                            label="global.download.pdf"
                            className="dropdown__item-btn"
                            bsStyle="link"
                        />
                        <Button
                            onClick={() => handleDepositListDownload(depositSelectedTab, filters, "xls")}
                            label="global.download.xls"
                            className="dropdown__item-btn"
                            bsStyle="link"
                        />
                    </Dropdown>
                </div>
            ) : (
                <>
                    <Button
                        onClick={() => handleDepositListDownload(depositSelectedTab, filters, "PDF")}
                        label="global.download.pdf"
                        className="dropdown__item-btn"
                        bsStyle="btn"
                    />
                    <Button
                        onClick={() => handleDepositListDownload(depositSelectedTab, filters, "xls")}
                        label="global.download.xls"
                        className="dropdown__item-btn"
                        bsStyle="btn"
                    />
                </>
            );
        }
        return null;
    };

    const handleClick = () => {
        dispatch(menuActions.menuShow());
    };

    const backButtonAction = () => {
        history.goBack();
    };

    const renderHeader = () => {
        // const { deposits, filters, active } = tabsData[tabIndex].downloadData;

        const renderMenuOption = () => (
            <>
                {isDesktop ? (
                    <>
                        {renderDownloadDropdown()}
                        <div className="toolbar-item add-button" key="add">
                            <CustomButton
                                func={() => dispatch(push("/deposits/requestForm"))}
                                className="btn btn-primary btn-block"
                                icon={isIOS ? "images/figma/icon/menu-ios-white.svg" : "images/figma/icon/nuevo.svg"}
                                text="deposits.make.new"
                                textType="I18N"
                            />
                        </div>
                    </>
                ) : (
                    <div className="toolbar-item commands-mobile">
                        <Button
                            className="toolbar-btn view-options"
                            onClick={handleClick}
                            image={isIOS ? "images/figma/icon/menu-ios-white.svg" : "images/figma/icon/menu-mobile.svg"}
                            label="view more options"
                            block={false}
                            bsStyle="link"
                        />
                    </div>
                )}
            </>
        );
        return (
            <CustomProductHead
                titleId="deposits.list.title"
                onBack={backButtonAction}
                addLinkToLabel="deposits.new"
                addLinkTo="/deposits/requestForm"
                className="deposits"
                renderMenuOption={renderMenuOption}>
                <div className="toolbar-item view-title">
                    <ViewTitle id="deposits.list.title" />
                </div>
            </CustomProductHead>
        );
    };
    return (
        <Fragment>
            <Notification scopeToShow="deposits" />
            {!isLoading && renderHeader()}
            <MainContainer showLoader={fetching || historicalDepositsFetching}>
                <div className="container">
                    <CustomTabs tabsData={tabsData} defaultTab={depositsScreenToHistorical ? 1 : 0}>
                        <DepositList
                            totalInvestedCapital={totalInvestedCapital}
                            deposits={deposits}
                            isLoading={fetching || historicalDepositsFetching}
                            {...props}
                        />
                        <HistoricalList {...props} />
                    </CustomTabs>
                </div>
            </MainContainer>
            {!isDesktop && (
                <>
                    <OptionsHeader>
                        <Button
                            className="dropdown__item-btn"
                            to="/deposits/requestForm"
                            onClick={() => dispatch(push("/deposits/requestForm"))}>
                            <I18n id="deposits.new.mobile" />
                        </Button>
                        {renderDownloadDropdown()}
                    </OptionsHeader>
                    <DepositOptions options={toggleOptions} handleClose={handleCloseOptions}>
                        <DepositAction
                            isDesktop={isDesktop}
                            labelKey="global.seeDetail"
                            to={`/deposits/historical/${toggleOptions[0]}`}
                        />
                        <DepositAction isDesktop={isDesktop} labelKey="global.download" handleClick={() => {}} />
                    </DepositOptions>
                </>
            )}
        </Fragment>
    );
};

Deposits.propTypes = {
    isRequestAvailable: bool,
    totalInvestedCapital: shape({ mainCurrency: number, USD: number }).isRequired,
    deposits: arrayOf(shape({})).isRequired,
    toggleOptions: arrayOf(string).isRequired,
    fetching: bool.isRequired,
    depositsScreenToHistorical: bool.isRequired,
    historicalDeposits: arrayOf(shape({})).isRequired,
    historicalDepositsFetching: bool.isRequired,
    dispatch: func.isRequired,
    noDepositFound: bool.isRequired,
    filters: shape({}).isRequired,
    isDesktop: bool.isRequired,
    fetchingDownload: bool.isRequired,
    history: shape({}).isRequired,
};

Deposits.defaultProps = {
    isRequestAvailable: true,
};

const mapStateToProps = (state) => ({
    deposits: depositsSelectors.getDeposits(state),
    noDepositFound: depositsSelectors.getNoDepositFound(state),
    historicalDeposits: depositsSelectors.getHistoricalDeposits(state),
    filters: depositsSelectors.getFilters(state),
    totalInvestedCapital: depositsSelectors.getTotalInvestedCapital(state),
    fetching: depositsSelectors.getFetching(state),
    historicalDepositsFetching: depositsSelectors.getHistoricalDepositsFetching(state),
    isRequestAvailable: sessionSelectors
        .getActiveEnvironmentForms(state, "deposits")
        .find(({ idForm }) => idForm === "depositRequest"),
    toggleOptions: depositsSelectors.getToggleOptions(state),
    depositsScreenToHistorical: depositsSelectors.getDepositsScreenToHistorical(state),
    fetchingDownload: depositsSelectors.getFetchingDownload(state),
});

export default connect(mapStateToProps)(Deposits);
